import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useGetCampaignById } from '@tinct/core-api';
import {
  MetricCard,
  LineChartCard,
  EmptyChartContent,
} from '@tinct/web-components';
import type { LineChartCardSeries, LineChartCardDataPoint } from '@tinct/web-components';
import {
  BarChart3,
  Timer,
  LineChart as LineChartIcon,
} from 'lucide-react';
import { ComparisonBadge, ComparisonBadges, TrackedEventsSection } from '../../shared';

const CHART_SERIES: LineChartCardSeries[] = [
  { key: 'personalized', label: 'Personalized', color: 'var(--color-chart-1, #6366f1)' },
  { key: 'original', label: 'Original', color: 'var(--color-chart-5, #f472b6)' },
];


export function AnalyticsTab() {
  const { id = '' } = useParams<{ id: string }>();

  const periodParams = useMemo(() => {
    const now = new Date();
    const start = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
    return {
      periodStart: `"${start.toISOString().split('T')[0]}"` as `"${number}-${number}-${number}"`,
      periodEnd: `"${now.toISOString().split('T')[0]}"` as `"${number}-${number}-${number}"`,
    };
  }, []);

  const { data: campaign } = useGetCampaignById({
    id,
    fields: {
      id: null,
      statistics: {
        fields: {
          visitsCount: null,
          periodVisitsVariationInPercent: {
            temporalPeriod: '"1w"'
          },
          sessionAverageTimeInSeconds: null,
          periodSessionAverageTimeVariationInPercent: {
            temporalPeriod: '"1w"'
          },
          originalSessionAverageTimeVariationInPercent: null,
          ctaClickPerSession: null,
          periodCtaClickPerSessionVariationInPercent: {
            temporalPeriod: '"1w"'
          },
          originalCtaClickPerSessionVariationInPercent: null,
          visitsTimeSeries: null,
          originalVisitsTimeSeries: null,
          trackedEvents: {
            fields: {
              blockId: null,
              eventName: null,
              count: null,
              ratio: null,
              lastSeenAt: null,
            },
          },
        },
        ...periodParams
      },
    },
  });

  const stats = campaign?.statistics;
  const visitsCount = stats?.visitsCount ?? 0;

  const trendOf = (value: number | null | undefined): 'up' | 'down' =>
    value !== null && value !== undefined && value >= 0 ? 'up' : 'down';

  const badgeOf = (value: number | null | undefined, label: string): ComparisonBadge | null =>
    value !== null && value !== undefined
      ? { value: `${Math.abs(Math.round(value))}%`, label, trend: trendOf(value) }
      : null;

  const visitsBadges = [badgeOf(stats?.periodVisitsVariationInPercent, 'vs last week')].filter(Boolean) as ComparisonBadge[];

  const avgTimeSec = stats?.sessionAverageTimeInSeconds;
  const avgTimeLabel = avgTimeSec !== null && avgTimeSec !== undefined
    ? avgTimeSec >= 60
      ? `${Math.floor(avgTimeSec / 60)}m ${Math.round(avgTimeSec % 60)}s`
      : `${Math.round(avgTimeSec)}s`
    : '—';
  const avgTimeBadges = [
    badgeOf(stats?.originalSessionAverageTimeVariationInPercent, 'vs Original'),
    badgeOf(stats?.periodSessionAverageTimeVariationInPercent, 'vs last week'),
  ].filter(Boolean) as ComparisonBadge[];

  const ctaClickPerSession = stats?.ctaClickPerSession;
  const ctaLabel = ctaClickPerSession !== null && ctaClickPerSession !== undefined
    ? `${Math.round(ctaClickPerSession * 10) / 10}/session`
    : '—';
  const ctaBadges = [
    badgeOf(stats?.originalCtaClickPerSessionVariationInPercent, 'vs Original'),
    badgeOf(stats?.periodCtaClickPerSessionVariationInPercent, 'vs last week'),
  ].filter(Boolean) as ComparisonBadge[];

  const chartData = useMemo<LineChartCardDataPoint[]>(() => {
    const personalizedSeries = stats?.visitsTimeSeries?.series ?? [];
    const originalSeries = stats?.originalVisitsTimeSeries?.series ?? [];
    const dateMap = new Map<string, { personalized: number; original: number }>();

    for (const point of personalizedSeries) {
      if (!point.date) continue;
      const date = point.date.split('T')[0];
      const entry = dateMap.get(date) ?? { personalized: 0, original: 0 };
      entry.personalized = point.value ?? 0;
      dateMap.set(date, entry);
    }

    for (const point of originalSeries) {
      if (!point.date) continue;
      const date = point.date.split('T')[0];
      const entry = dateMap.get(date) ?? { personalized: 0, original: 0 };
      entry.original = point.value ?? 0;
      dateMap.set(date, entry);
    }

    return Array.from(dateMap.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([date, values]) => ({ date, ...values }));
  }, [stats?.visitsTimeSeries, stats?.originalVisitsTimeSeries]);

  return (
    <div className="flex flex-col gap-3">
      {/* Metric Cards */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <MetricCard
          headerText="Visits"
          statisticText={`${visitsCount}`}
          icon={<BarChart3 className="size-4 text-muted-foreground" />}
          slot={visitsBadges.length > 0 ? <ComparisonBadges badges={visitsBadges} /> : undefined}
        />
        <MetricCard
          headerText="Average time"
          statisticText={avgTimeLabel}
          icon={<Timer className="size-4 text-muted-foreground" />}
          slot={avgTimeBadges.length > 0 ? <ComparisonBadges badges={avgTimeBadges} /> : undefined}
        />
        <MetricCard
          headerText="CTA Clicks"
          statisticText={ctaLabel}
          slot={ctaBadges.length > 0 ? <ComparisonBadges badges={ctaBadges} /> : undefined}
        />
      </div>

      {/* Line Chart */}
      <LineChartCard
        headerIcon={<LineChartIcon className="size-4" />}
        headerLabel="Chart"
        title="Visits over time"
        description="Comparison between personalized pages and the original page."
        series={CHART_SERIES}
        data={chartData}
        emptyState={
          <EmptyChartContent
            title="No data yet"
            description="Your statistics, performance, and insights will be displayed here once your campaign is active."
          />
        }
      />

      {/* Tracked events */}
      <TrackedEventsSection
        trackedEvents={stats?.trackedEvents}
        isReady={campaign !== undefined}
      />
    </div>
  );
}
